/**
 * WebSocket 单连接管理器
 *
 * 特性：
 * 1. 全局只维护一个 WebSocket 连接
 * 2. 连接建立后可多次调用 send 发送消息
 * 3. 主动断开：disconnect() 不会触发自动重连
 * 4. 网络异常断开：自动重连，指数退避，最大重试次数可配
 * 5. 断线期间的消息进入队列，重连后自动发送
 *
 * 用法：
 *   import WsSocket from '@/utils/websocket'
 *
 *   // 连接
 *   WsSocket.connect('ws://127.0.0.1:8080/ws', {
 *     onOpen: () => {},
 *     onMessage: (data) => {},
 *     onClose: () => {},
 *     onError: () => {}
 *   })
 *
 *   // 发送消息（可多次调用）
 *   WsSocket.send({ type: 'ping' })
 *   WsSocket.send('plain text')
 *
 *   // 主动断开
 *   WsSocket.disconnect()
 */

// 连接状态
const READY_STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3
}

class WsSocketManager {
  constructor() {
    this.ws = null
    this.url = ''
    this.opts = {}
    // 断线期间积压的消息队列
    this.messageQueue = []
    // 是否为主动断开（主动断开不触发重连）
    this.manualClose = false
    // 重连相关
    this.reconnectCount = 0
    this.reconnectTimer = null
    // 引用计数：多个调用方共享同一连接，全部断开才真正关闭
    this.refCount = 0
  }

  /**
   * 建立连接（多次调用同一地址会复用连接并累加引用计数）
   * @param {string} url    WebSocket 地址
   * @param {object} options 回调配置
   *   - onOpen:    连接成功回调
   *   - onMessage: 收到消息回调（参数为 data）
   *   - onClose:   连接关闭回调
   *   - onError:   连接错误回调
   *   - maxReconnect: 最大重连次数，默认 10
   *   - reconnectInterval: 基础重连间隔(ms)，默认 3000
   */
  connect(url, options = {}) {
    this.refCount++
    // 同一地址且连接存活/连接中：复用，不重复创建
    if (
      this.ws &&
      this.url === url &&
      (this.ws.readyState === READY_STATE.OPEN || this.ws.readyState === READY_STATE.CONNECTING)
    ) {
      this.manualClose = false
      this.opts = Object.assign({}, this.opts, options)
      return
    }
    this.url = url
    this.opts = options
    this.manualClose = false
    this.reconnectCount = 0
    this._create()
  }

  _create() {
    // 若已有连接且未关闭，先清理
    if (this.ws) {
      this.ws.onopen = null
      this.ws.onmessage = null
      this.ws.onclose = null
      this.ws.onerror = null
      if (this.ws.readyState === READY_STATE.OPEN || this.ws.readyState === READY_STATE.CONNECTING) {
        this.ws.close()
      }
    }

    try {
      this.ws = new WebSocket(this.url)
    } catch (e) {
      this._handleError(e)
      return
    }

    this.ws.onopen = (e) => {
      this.reconnectCount = 0
      // 发送断线期间积压的消息
      if (this.messageQueue.length) {
        const queue = this.messageQueue.splice(0)
        queue.forEach((msg) => this.send(msg))
      }
      this.opts.onOpen && this.opts.onOpen(e)
    }

    this.ws.onmessage = (e) => {
      let data = e.data
      // 尝试 JSON 解析，失败则返回原始字符串
      try {
        data = JSON.parse(e.data)
      } catch (_) {
        // 非 JSON，保持原始值
      }
      this.opts.onMessage && this.opts.onMessage(data)
    }

    this.ws.onclose = (e) => {
      this.opts.onClose && this.opts.onClose(e)
      // 非主动断开时自动重连
      if (!this.manualClose) {
        this._reconnect()
      }
    }

    this.ws.onerror = (e) => {
      this._handleError(e)
    }
  }

  /**
   * 发送消息
   * @param {string|object} data 字符串或对象（对象会自动 JSON.stringify）
   */
  send(data) {
    const payload = typeof data === 'string' ? data : JSON.stringify(data)
    if (this.ws && this.ws.readyState === READY_STATE.OPEN) {
      this.ws.send(payload)
    } else {
      // 未连接或正在重连，消息进入队列
      this.messageQueue.push(payload)
    }
  }

  /**
   * 释放连接引用（引用计数归零时才真正断开，不触发自动重连）
   */
  disconnect() {
    this.refCount = Math.max(0, this.refCount - 1)
    // 还有其他调用方在用，保持连接
    if (this.refCount > 0) {
      return
    }
    this.manualClose = true
    this._clearReconnectTimer()
    this.messageQueue = []
    if (this.ws) {
      this.ws.onclose = null // 移除 onclose，防止触发 _reconnect
      if (this.ws.readyState === READY_STATE.OPEN || this.ws.readyState === READY_STATE.CONNECTING) {
        this.ws.close()
      }
      this.ws = null
    }
  }

  /**
   * 获取当前连接状态
   * @returns {boolean} true 表示已连接
   */
  isConnected() {
    return this.ws && this.ws.readyState === READY_STATE.OPEN
  }

  // ---- 内部方法 ----

  _handleError(e) {
    this.opts.onError && this.opts.onError(e)
    // onclose 会紧随 onerror 触发，重连逻辑统一放在 onclose 中
  }

  _reconnect() {
    const maxReconnect = this.opts.maxReconnect || 10
    if (this.reconnectCount >= maxReconnect) {
      return
    }
    this.reconnectCount++
    // 指数退避：基础间隔 * 2^(count-1)，上限 30s
    const baseInterval = this.opts.reconnectInterval || 3000
    const delay = Math.min(baseInterval * Math.pow(2, this.reconnectCount - 1), 30000)
    this._clearReconnectTimer()
    this.reconnectTimer = setTimeout(() => {
      this._create()
    }, delay)
  }

  _clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}

// 全局单例
export default new WsSocketManager()
